// @flow
import React, { useState, useEffect } from "react";
import ReactQuill, { Quill } from "react-quill";
import type { DeltaOperation, DeltaStatic, Sources } from "quill";

import "react-quill/dist/quill.snow.css";
import "./RichTextField.styles.css";

interface Props {
  defaultValue: string;
  onChange?: (arg: string) => void;
}

function isValidUrl(string: string) {
  const regex =
    /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;
  return regex.test(string);
}

export const RichTextField = ({ defaultValue, onChange }: Props) => {
  const attributors = ["style/align", "attribute/direction"];
  attributors.forEach((attributor) => {
    const Attributor = Quill.import(`attributors/${attributor}`);
    Quill.register(Attributor, true);
  });

  const Delta = Quill.import("delta");
  const [value, setValue] = useState(defaultValue);

  useEffect(() => {
    setValue(defaultValue);
  }, [defaultValue]);

  const handleChange = (
    _value: string,
    delta: DeltaStatic,
    source: Sources,
    editor: ReactQuill.UnprivilegedEditor
  ) => {
    if (source !== "user") return;

    let html = editor.getHTML();
    const deltaFull = editor.getContents();

    // **1. Remove all <p><br></p> that appear before <ul> or <ol>**
    html = html.replace(/<p><br><\/p>(\s*<ul|<ol)/g, "$1");

    // **2. Remove unnecessary <p> wrapping lists**
    html = html.replace(/<p>\s*(<(ul|ol)[\s\S]*?<\/\2>)\s*<\/p>/g, "$1");

    // **3. Remove consecutive <br> (keep only one)**
    html = html.replace(/(<br\s*\/?>\s*){2,}/g, "<br>");

    // **4. Fix empty paragraphs that Quill sometimes inserts**
    html = html.replace(/<p>\s*<\/p>/g, "");

    const ops: DeltaOperation[] = [];
    deltaFull.ops?.forEach((op) => {
      if (
        op.attributes &&
        op.attributes.link &&
        !isValidUrl(op.attributes.link)
      ) {
        ops.push({
          insert: op.insert,
          attributes: {
            ...op.attributes,
            link: null,
          },
        });
      } else {
        ops.push(op);
      }
    });

    // Update state only if there's an actual change
    if (html !== value) {
      setValue(html);
      onChange?.(html);
    }
  };

  const modules = {
    toolbar: [
      [{ header: [1, 2, 3, false] }],
      ["bold", "italic", "underline"],
      [{ align: [] }],
      ["link"],
      [{ list: "ordered" }],
      [{ list: "bullet" }],
      [{ direction: "rtl" }],
      ["clean"],
    ],
  };

  return (
    <div style={{ minHeight: "300px" }}>
      <ReactQuill
        theme="snow"
        style={{ minHeight: "300px" }}
        modules={modules}
        ref={(el) => {
          if (el) {
            const quill = el.getEditor();

            quill.root.dataset.placeholder = "Enter your text here...";
            quill.clipboard.addMatcher(Node.ELEMENT_NODE, (node, delta) => {
              const ops: any = [];
              delta.ops?.forEach((op) => {
                if (
                  op.insert &&
                  typeof op.insert === "object" &&
                  op.insert.image
                ) {
                  console.log("Image paste detected, preventing insert.");
                } else {
                  ops.push(op);
                }
              });
              return new Delta(ops);
            });
          }
        }}
        value={value}
        onChange={handleChange}
      />
    </div>
  );
};
